import React, {
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageContainer, Section, StatGrid } from '../components/AdminContentWrapper';
import { DataContext } from '../../../context/DataContext';
import {
  listPolicies,
  useSecurityPoliciesService
} from '../../../services/SecurityPoliciesService';
import { getPermissionsCatalog } from '../../../services/SecurityRolesService';
import HelpIcon from '../../../components/help/HelpIcon';

// Iconos rotados para las tarjetas de política -- puramente decorativo.
const POLICY_ICONS = [
  <KeyIcon sx={{ color: 'var(--wb-accent)' }} />,
  <ShieldOutlinedIcon sx={{ color: 'var(--wb-success)' }} />,
  <VerifiedUserIcon sx={{ color: 'var(--wb-warning)' }} />
];

const DOMAIN_LABELS = {
  security: 'Seguridad',
  connections: 'Conexiones',
  semantic: 'Modelo semántico',
  agents: 'Agentes'
};

const domainLabel = (domainKey) => DOMAIN_LABELS[domainKey] || domainKey;

const emptyFormState = { name: '', description: '', permissions: [] };

// Una Política es un bundle plano de permission keys del catálogo --
// mismo formulario que antes usaba RoleFormDialog en SecurityRoles.jsx,
// ahora movido acá porque las Políticas son las que tienen permisos, no
// los Roles (que ahora componen Políticas, ver SecurityRoles.jsx).
const PolicyFormDialog = ({
  open,
  mode,
  initialPolicy,
  catalog,
  catalogLoading,
  onClose,
  onSubmit,
  isSubmitting
}) => {
  const [formState, setFormState] = useState(emptyFormState);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'edit' && initialPolicy) {
      setFormState({
        name: initialPolicy.name || '',
        description: initialPolicy.description || '',
        permissions: Array.isArray(initialPolicy.permissions) ? [...initialPolicy.permissions] : []
      });
    } else {
      setFormState(emptyFormState);
    }
    setError('');
  }, [open, mode, initialPolicy]);

  const togglePermission = (key) => {
    setFormState((prev) => {
      const has = prev.permissions.includes(key);
      return {
        ...prev,
        permissions: has
          ? prev.permissions.filter((permission) => permission !== key)
          : [...prev.permissions, key]
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formState.name.trim()) {
      setError('El nombre de la política es obligatorio.');
      return;
    }
    setError('');
    await onSubmit(formState);
  };

  const domainKeys = catalog ? Object.keys(catalog) : [];

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: {
          backgroundColor: 'var(--wb-bg-surface)',
          border: '1px solid var(--wb-border-16)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>
        {mode === 'edit' ? 'Editar política' : 'Nueva política'}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'var(--wb-border)' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          margin="normal"
          label="Nombre"
          fullWidth
          value={formState.name}
          onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="seguridad" topic="politicas" anchor="nombre" />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputBase-root': { color: 'var(--wb-text-title)', backgroundColor: 'var(--wb-bg-overlay-90)' },
            '& .MuiInputLabel-root': { color: 'var(--wb-muted-80)' }
          }}
        />
        <TextField
          margin="normal"
          label="Descripción"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          value={formState.description}
          onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="seguridad" topic="politicas" anchor="descripción" />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputBase-root': { color: 'var(--wb-text-title)', backgroundColor: 'var(--wb-bg-overlay-90)' },
            '& .MuiInputLabel-root': { color: 'var(--wb-muted-80)' }
          }}
        />

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
            <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600 }}>
              Permisos
            </Typography>
            <HelpIcon module="seguridad" topic="politicas" anchor="permisos" />
          </Stack>

          {catalogLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography sx={{ color: 'var(--wb-muted-80)' }}>
                Cargando catálogo de permisos...
              </Typography>
            </Stack>
          )}

          {!catalogLoading && domainKeys.length === 0 && (
            <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
              No se pudo cargar el catálogo de permisos. Puedes guardar la política sin permisos y editarla luego.
            </Alert>
          )}

          {!catalogLoading && domainKeys.map((domainKey) => {
            const items = Array.isArray(catalog[domainKey]) ? catalog[domainKey] : [];
            const selectedInDomain = items.filter((item) => formState.permissions.includes(item.key)).length;
            return (
              <Accordion
                key={domainKey}
                disableGutters
                sx={{
                  backgroundColor: 'var(--wb-bg-panel)',
                  color: 'var(--wb-text)',
                  border: '1px solid var(--wb-border)',
                  mb: 1,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--wb-muted)' }} />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 600 }}>{domainLabel(domainKey)}</Typography>
                    {selectedInDomain > 0 && (
                      <Chip
                        size="small"
                        label={selectedInDomain}
                        sx={{ backgroundColor: 'var(--wb-accent-18)', color: 'var(--wb-accent)' }}
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {items.map((item) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            checked={formState.permissions.includes(item.key)}
                            onChange={() => togglePermission(item.key)}
                            sx={{ color: 'var(--wb-muted-60)', '&.Mui-checked': { color: 'var(--wb-accent)' } }}
                          />
                        }
                        label={item.label || item.key}
                      />
                    ))}
                    {items.length === 0 && (
                      <Typography sx={{ color: 'var(--wb-muted-70)' }}>
                        Sin permisos definidos en este dominio.
                      </Typography>
                    )}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ color: 'var(--wb-muted)' }}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
        >
          {isSubmitting ? 'Guardando...' : mode === 'edit' ? 'Guardar cambios' : 'Crear política'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SecurityPolicies = () => {
  const { isPlatformRoot, isTenantAdmin, userData, hasPermission } = useContext(DataContext);
  const canManagePolicyCatalog = Boolean(isPlatformRoot || isTenantAdmin);
  const canEditPolicies = canManagePolicyCatalog || hasPermission('security.policies.manage');

  const accessChecked = Boolean(userData);

  const { createPolicy, updatePolicy, deletePolicy } = useSecurityPoliciesService();

  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policiesError, setPoliciesError] = useState('');

  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const [feedback, setFeedback] = useState(null);
  const [dialogState, setDialogState] = useState({ open: false, mode: 'create', policy: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPolicies = useCallback(async () => {
    if (!canManagePolicyCatalog) {
      return;
    }
    setPoliciesLoading(true);
    setPoliciesError('');
    try {
      const response = await listPolicies();
      setPolicies(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      setPoliciesError(error?.message || 'No se pudo obtener el listado de políticas.');
    } finally {
      setPoliciesLoading(false);
    }
  }, [canManagePolicyCatalog]);

  const fetchCatalog = useCallback(async () => {
    if (!canManagePolicyCatalog) {
      return;
    }
    setCatalogLoading(true);
    try {
      const response = await getPermissionsCatalog();
      setCatalog(response && typeof response === 'object' ? response : {});
    } catch (error) {
      console.warn('⚠️ No se pudo cargar el catálogo de permisos:', error);
      setCatalog({});
    } finally {
      setCatalogLoading(false);
    }
  }, [canManagePolicyCatalog]);

  useEffect(() => {
    fetchPolicies();
    fetchCatalog();
  }, [fetchPolicies, fetchCatalog]);

  const permissionLabelMap = React.useMemo(() => {
    const map = {};
    if (catalog) {
      Object.values(catalog).forEach((items) => {
        if (Array.isArray(items)) {
          items.forEach((item) => {
            if (item?.key) {
              map[item.key] = item.label || item.key;
            }
          });
        }
      });
    }
    return map;
  }, [catalog]);

  const customPoliciesCount = React.useMemo(
    () => policies.filter((policy) => !policy.is_system).length,
    [policies]
  );

  const handleOpenCreate = () => {
    setDialogState({ open: true, mode: 'create', policy: null });
  };

  const handleOpenEdit = (policy) => {
    setDialogState({ open: true, mode: 'edit', policy });
  };

  const handleCloseDialog = () => {
    if (isSubmitting) {
      return;
    }
    setDialogState({ open: false, mode: 'create', policy: null });
  };

  const handleSubmitDialog = async (formState) => {
    setIsSubmitting(true);
    setFeedback(null);
    try {
      if (dialogState.mode === 'edit' && dialogState.policy) {
        await updatePolicy(dialogState.policy.policy_id, formState);
        setFeedback({ severity: 'success', message: `Se actualizó la política "${formState.name}".` });
      } else {
        await createPolicy(formState);
        setFeedback({ severity: 'success', message: `Se creó la política "${formState.name}".` });
      }
      setDialogState({ open: false, mode: 'create', policy: null });
      await fetchPolicies();
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'No se pudo guardar la política.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setIsDeleting(true);
    setFeedback(null);
    try {
      await deletePolicy(deleteTarget.policy_id);
      setFeedback({ severity: 'success', message: `Se eliminó la política "${deleteTarget.name}".` });
      setDeleteTarget(null);
      await fetchPolicies();
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'No se pudo eliminar la política.'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!accessChecked) {
    return (
      <PageContainer title="Políticas" description="Define bundles de permisos reutilizables y adjuntables.">
        <Section title="Cargando acceso">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Verificando permisos...</Typography>
          </Stack>
        </Section>
      </PageContainer>
    );
  }

  if (!canManagePolicyCatalog) {
    return (
      <PageContainer
        title="Políticas"
        description="Solo administradores de la cuenta pueden gestionar el catálogo de políticas."
      >
        <Section title="Acceso restringido">
          <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
            Necesitas privilegios de administrador (de tu cuenta o de la plataforma) para acceder a esta sección.
          </Alert>
        </Section>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Políticas"
      description="Bundles de permisos reutilizables -- de sistema (inmutables) o personalizados por tu cuenta -- adjuntables a Roles y Grupos."
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          color="info"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchPolicies(); fetchCatalog(); }}
          disabled={policiesLoading}
          sx={{ textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: 'var(--wb-accent)' }}
        >
          Actualizar
        </Button>,
        canEditPolicies && (
          <Button
            key="policy"
            variant="outlined"
            color="secondary"
            onClick={handleOpenCreate}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Nueva política
          </Button>
        )
      ].filter(Boolean)}
    >
      {feedback && (
        <Box sx={{ mb: 3 }}>
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        </Box>
      )}

      <StatGrid
        items={[
          { label: 'Políticas configuradas', value: String(policies.length), trend: `${customPoliciesCount} personalizadas` },
          { label: 'Políticas de sistema', value: String(policies.length - customPoliciesCount), trend: 'Inmutables' },
          { label: 'Políticas personalizadas', value: String(customPoliciesCount), trend: 'Editables por tu cuenta' }
        ]}
      />

      {policiesError && (
        <Alert severity="error" sx={{ mb: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}>
          {policiesError}
        </Alert>
      )}

      <Section
        title="Catálogo de políticas"
        subtitle="Las de sistema no se pueden editar ni borrar -- solo adjuntar a Roles o Grupos"
      >
        {policiesLoading && policies.length === 0 && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Cargando políticas...</Typography>
          </Stack>
        )}

        {!policiesLoading && policies.length === 0 && !policiesError && (
          <Typography sx={{ color: 'var(--wb-muted-70)', py: 2 }}>
            No hay políticas configuradas todavía.
          </Typography>
        )}

        <Grid container spacing={3}>
          {policies.map((policy, index) => {
            const permissions = Array.isArray(policy.permissions) ? policy.permissions : [];
            return (
              <Grid item xs={12} md={4} key={policy.policy_id}>
                <Box
                  sx={{
                    backgroundColor: 'var(--wb-bg-surface)',
                    borderRadius: 2,
                    border: '1px solid var(--wb-border-10)',
                    p: 3,
                    height: '100%'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {POLICY_ICONS[index % POLICY_ICONS.length]}
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{policy.name}</Typography>
                          {policy.is_system && (
                            <Chip
                              label="Política de sistema"
                              size="small"
                              sx={{ backgroundColor: 'var(--wb-border-20)', color: 'var(--wb-text-label)' }}
                            />
                          )}
                        </Stack>
                        <Typography sx={{ color: 'var(--wb-muted-76)', fontSize: '0.85rem' }}>
                          {policy.description}
                        </Typography>
                      </Box>
                    </Stack>
                    {!policy.is_system && canEditPolicies && (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Editar política">
                          <IconButton size="small" onClick={() => handleOpenEdit(policy)} sx={{ color: 'var(--wb-accent)' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar política">
                          <IconButton size="small" onClick={() => setDeleteTarget(policy)} sx={{ color: 'var(--wb-danger)' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                    {permissions.length === 0 && (
                      <Typography sx={{ color: 'var(--wb-muted-60)', fontSize: '0.8rem' }}>
                        Sin permisos asignados
                      </Typography>
                    )}
                    {permissions.map((permissionKey) => (
                      <Chip
                        key={permissionKey}
                        label={permissionLabelMap[permissionKey] || permissionKey}
                        size="small"
                        sx={{
                          backgroundColor: 'var(--wb-border-16)',
                          color: 'var(--wb-text-label)',
                          width: 'fit-content'
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Section>

      <PolicyFormDialog
        open={dialogState.open}
        mode={dialogState.mode}
        initialPolicy={dialogState.policy}
        catalog={catalog}
        catalogLoading={catalogLoading}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        isSubmitting={isSubmitting}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (isDeleting ? undefined : setDeleteTarget(null))}
        PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface)', border: '1px solid var(--wb-border-16)' } }}
      >
        <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>Eliminar política</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--wb-text-label)' }}>
            ¿Seguro que deseas eliminar la política "{deleteTarget?.name}"? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting} sx={{ color: 'var(--wb-muted)' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default SecurityPolicies;
